import { Button } from "@mui/material";
import Papa from "papaparse";
import * as xlsx from "xlsx";
import colors from "../Constants/colors";
import { setDefaultCombination } from "../features/chatbots/chatbotSlice";
// Lazy-load heavy/ESM-dependent modules inside functions to avoid Jest transform issues


export const handleMakeDefaultRetrieval = (
  dispatch,
  chatbot,
  id,
  typeKey = "retrieval_0"
) => {
  const defaultCombination = { ...chatbot.defaultCombination };
  // set the desired default key

  defaultCombination[typeKey] = id;

  // enforce exclusivity rules where applicable
  if (typeKey.startsWith("generation")) {
    // selecting a generation default clears finetune
    if (defaultCombination["finetune"]) {
      delete defaultCombination["finetune"];
    }
  }
  if (typeKey === "finetune") {
    // selecting finetune clears generation_1
    if (defaultCombination["generation_1"]) {
      delete defaultCombination["generation_1"];
    }
  }
  dispatch(setDefaultCombination(defaultCombination));
};

export const handleRemoveDefaultRetrieval = (
  dispatch,
  chatbot,
  id,
  typeKey = "retrieval_0"
) => {
  const defaultCombination = { ...chatbot.defaultCombination };
  // clear the desired default key
  defaultCombination[typeKey] = "";

  // maintain the same exclusivity semantics as the setter for consistency
  if (typeKey.startsWith("generation")) {
    if (defaultCombination["finetune"]) {
      delete defaultCombination["finetune"];
    }
  }
  if (typeKey === "finetune") {
    if (defaultCombination["generation_1"]) {
      delete defaultCombination["generation_1"];
    }
  }

  dispatch(setDefaultCombination(defaultCombination));
};

export const mapRetrievalMethodHyperparameter = (sequence) => {
  const mapping = {
    basic: "None",
    multi_query: "Multi Phrasing",
    hyde: "HyDE",
    query_expansion: "Expansion",
    complex_to_simple: "Deconstruction",
    iterative_deconstruction: "Iterative Deconstruction",
    "user query": "User Query",
  };

  if (sequence) {
    // Check if the sequence contains the arrow symbol
    if (sequence.includes("→")) {
      return sequence
        .split("→")
        .map((item) => mapping[item.toLowerCase()] || item)
        .join(" → ");
    }

    // Return the original sequence if no arrow symbol is found
    return mapping[sequence.toLowerCase()];
  } else {
    return "N/A";
  }
};

export const mapDocumentParser = (parser) => {
  const mapping = {
    llama_parse: "Llama Parse",
    recursive_character_text_splitter: "Basic",
    unstructuredio: "Unstructured.io",
    "gpt-4.1": "gpt 4.1",
    "gpt-4.1-mini": "gpt 4.1 mini",
    "gemini-2.0-flash": "gemini 2.0 flash",
    "gemini-2.5-pro": "gemini 2.5 pro",
    "gemini-2.5-flash": "gemini 2.5 flash",
    "claude-3-7-sonnet-latest": "claude 3.7 sonnet latest",
    "claude-sonnet-4-20250514": "claude sonnet 4",
  };

  return mapping[parser] || "N/A";
};

export const formatValue = (value) => {
  if (value === 0) return value; // Return 0 as is

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = (value / Math.pow(10, exponent)).toFixed(2); // Mantissa with 2 decimal places

  // If the exponent is between -2 and 2 (inclusive), return the rounded value with 2 decimal places
  if (exponent >= -2 && exponent <= 2) {
    return Number(value).toFixed(2);
  }

  // Otherwise, return the mantissa and exponent in scientific notation
  return (
    <>
      {mantissa} &times; 10<sup>{exponent}</sup>
    </>
  );
};

export const formattedDate = (uDate) => {
  if (!uDate) return "No Date Available"; // Handle null or undefined dates

  const date = new Date(uDate.$date || uDate); // Support both MongoDB-style objects and ISO strings

  const fDate = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  return fDate;
};

// helper function to check csv file header consistency
export function validateCSV(fileContent) {
  // Use Papa Parse to preview the first few rows of the file.
  const result = Papa.parse(fileContent, {
    header: false, // Do not treat any row as header for this preview
    preview: 5, // Process first 5 rows to check for multi-row headers and data consistency
    skipEmptyLines: true,
  });

  // Ensure that there are at least 2 rows (header and one data row)
  if (result.data.length < 2) {
    return {
      error: "Not enough rows in CSV for header check",
      headerRow: [],
      firstDataRow: [],
    };
  }

  // Extract the header row and first data row
  const headerRow = result.data[0];
  const firstDataRow = result.data[1];
  const headerLength = headerRow.length;
  const firstDataRowLength = firstDataRow.length;
  const isConsistent = headerLength === firstDataRowLength;

  // NEW: Check for multi-row header structure (same logic as Excel)
  if (result.data.length >= 3) {
    const firstRowNonEmpty = headerRow.filter(h => h !== null && h !== undefined && h.toString().trim() !== "").length;
    const secondRowNonEmpty = firstDataRow.filter(h => h !== null && h !== undefined && h.toString().trim() !== "").length;

    // If second row has significantly more headers than first row, it's likely a multi-row header structure
    if (secondRowNonEmpty > firstRowNonEmpty && secondRowNonEmpty >= 2 && firstRowNonEmpty <= 2) {
      return {
        error: "Invalid CSV format. Please use a standard format with headers in the first row only.",
        headerRow,
        firstDataRow,
        headerLength,
        firstDataRowLength,
        isConsistent: false,
      };
    }

    // Additional check: if first row has very sparse data but second row is dense, reject
    if (firstRowNonEmpty === 1 && secondRowNonEmpty >= 3) {
      return {
        error: "Invalid CSV format. Please format with headers in the first row only.",
        headerRow,
        firstDataRow,
        headerLength,
        firstDataRowLength,
        isConsistent: false,
      };
    }
  }

  // NEW: Check all data rows for column count vs header count
  const nonEmptyHeaderCount = headerRow.filter(h => h !== null && h !== undefined && h.toString().trim() !== "").length;

  for (let i = 1; i < result.data.length; i++) {
    const dataRow = result.data[i];
    const actualDataLength = dataRow.length;
    const nonEmptyDataCount = dataRow.filter(val => val !== null && val !== undefined && val.toString().trim() !== "").length;

    // If any data row has more actual columns than total headers, reject the file
    if (actualDataLength > headerLength) {
      return {
        error: "Invalid CSV format. Please ensure all columns have headers.",
        headerRow,
        firstDataRow,
        headerLength,
        firstDataRowLength,
        isConsistent: false,
      };
    }

    // Also check if non-empty data exceeds non-empty headers
    if (nonEmptyDataCount > nonEmptyHeaderCount) {
      return {
        error: "Invalid CSV format. Please ensure all data columns have headers.",
        headerRow,
        firstDataRow,
        headerLength,
        firstDataRowLength,
        isConsistent: false,
      };
    }
  }

  return {
    headerRow,
    firstDataRow,
    headerLength,
    firstDataRowLength,
    isConsistent,
  };
}

// helper function to check the values in the 3rd row (index 2) of the specified column
export const getColumnDefaultType = (file_data, columnName) => {
  if (file_data.length < 3) {
    // If the data has less than 3 rows, default to "Metadata column"
    return "Metadata column";
  }

  const value = file_data[2][columnName];
  if (value?.length > 200) {
    return "Text column";
  }
  if (!value?.includes(",")) {
    return "Metadata column"; // Not comma-separated
  }
  return value.split(",").every((part) => part.trim().split(/\s+/).length <= 4)
    ? "Metadata column"
    : "Text column";
};

export const getColumnDefaultTypeExcel = (columnData) => {
  // columnData is an array of values for this column
  if (!columnData || columnData.length < 3) {
    return "Metadata column";
  }

  // Check first few non-empty values to determine type
  const sampleSize = Math.min(5, columnData.length);
  const samples = columnData
    .filter((value) => value !== undefined && value !== null && value !== "")
    .slice(0, sampleSize);

  if (samples?.length === 0) {
    return "Metadata column";
  }

  // Helper function to check if a value looks like text content
  const isTextContent = (value) => {
    if (typeof value !== "string") {
      value = String(value);
    }

    // If length is greater than 200 characters, it's likely text content
    if (value?.length > 200) {
      return true;
    }

    // If it has multiple sentences or contains punctuation typical of text
    if (value.includes(".") && value.includes(" ")) {
      const sentences = value.split(".").filter((s) => s.trim().length > 0);
      if (sentences.length > 1) {
        return true;
      }
    }

    // If it has many words (more than 4)
    const words = value.trim().split(/\s+/);
    return words?.length > 4;
  };

  // Check if any sample looks like text content
  const hasTextContent = samples.some(isTextContent);

  // Check if values are consistently structured like metadata
  const isConsistentFormat = samples.every((value) => {
    if (typeof value !== "string") {
      value = String(value);
    }

    // Check for common metadata patterns
    const isShortValue = value?.length < 50;
    const hasNoSentences = !value.includes(".");
    const isSingleWord = value.trim().split(/\s+/).length <= 2;
    const isNumericOrDate = !isNaN(value) || !isNaN(Date.parse(value));

    return isShortValue || hasNoSentences || isSingleWord || isNumericOrDate;
  });

  // Decision logic
  if (hasTextContent && !isConsistentFormat) {
    return "Text column";
  }

  // Check for potential comma-separated metadata
  const hasCommaValues = samples.some(
    (value) => typeof value === "string" && value.includes(",")
  );

  if (hasCommaValues) {
    // Check if comma-separated values are short (metadata-like)
    const isShortCommaSeparated = samples.every((value) => {
      if (typeof value !== "string") return true;
      return value
        .split(",")
        .every((part) => part.trim().split(/\s+/).length <= 4);
    });

    return isShortCommaSeparated ? "Metadata column" : "Text column";
  }

  return "Metadata column";
};

export const parseExcelData = async (file, setUploadDatasetLoading, data, onProgress) => {
  try {
    // Yield to UI thread before heavy work
    await new Promise((r) => setTimeout(r, 0));
    // Create a workbook from the array buffer (sync inside, but preceded by yield)
    const workbook = xlsx.read(file, { type: "array" });

    // Iterate through each sheet name
    const sheetsData = {};
    const totalSheets = workbook.SheetNames.length;
    let index = 0;
    for (const sheetName of workbook.SheetNames) {
      // Yield between sheets to keep UI responsive
      await new Promise((r) => setTimeout(r, 0));
      const worksheet = workbook.Sheets[sheetName];

      try {
        // Lightweight pass: read only first N preview rows for UI (avoid freezing on massive first column)
        const PREVIEW_ROWS = 5; // small preview for UI
        const preview_data = xlsx.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          range: 0,
        }).slice(0, PREVIEW_ROWS + 1); // include header row + PREVIEW_ROWS

        if (preview_data.length === 0) {
          console.warn(`Sheet ${sheetName} is empty`);
          continue;
        }

        // Validate header count vs data column count
        if (preview_data.length >= 2) {
          const headerRow = preview_data[0]; // Always use first row as headers
          const headerCount = headerRow.length;
          const nonEmptyHeaderCount = headerRow.filter(h => h !== null && h !== undefined && h.toString().trim() !== "").length;

          // Detect multi-row header structure and reject it
          if (preview_data.length >= 3) {
            const firstRowNonEmpty = headerRow.filter(h => h !== null && h !== undefined && h.toString().trim() !== "").length;
            const secondRowNonEmpty = preview_data[1].filter(h => h !== null && h !== undefined && h.toString().trim() !== "").length;

            // If second row has significantly more headers than first row, it's likely a multi-row header structure
            if (secondRowNonEmpty > firstRowNonEmpty && secondRowNonEmpty >= 2 && firstRowNonEmpty <= 2) {
              throw new Error(`Invalid Excel format in sheet ${sheetName}. Please use a standard format with headers in the first row only.`);
            }

            // Additional check: if first row has very sparse data but second row is dense, reject
            if (firstRowNonEmpty === 1 && secondRowNonEmpty >= 3) {
              throw new Error(`Invalid Excel format in sheet ${sheetName}. Please format with headers in the first row only.`);
            }
          }

          // Check each data row for column count vs header count
          for (let i = 1; i < preview_data.length; i++) {
            const dataRow = preview_data[i];
            const actualDataLength = dataRow.length;
            const nonEmptyDataCount = dataRow.filter(val => val !== null && val !== undefined && val.toString().trim() !== "").length;

            // If any data row has more actual columns than total headers, reject the file
            if (actualDataLength > headerCount) {
              throw new Error(`Invalid Excel format in sheet ${sheetName}. Please ensure all columns have headers.`);
            }

            // Also check if non-empty data exceeds non-empty headers
            if (nonEmptyDataCount > nonEmptyHeaderCount) {
              throw new Error(`Invalid Excel format in sheet ${sheetName}. Please ensure all data columns have headers.`);
            }
          }
        }

        // Extract headers from the first row in preview
        const headers = preview_data[0];

        // Compute columnTypes using sampled preview rows only (fast)
        const previewColumnSamples = headers.map((_, index) =>
          preview_data.slice(1).map((row) => row[index] || "")
        );
        const columnTypes = headers.reduce((acc, header, index) => {
          acc[header] = getColumnDefaultTypeExcel(previewColumnSamples[index]);
          return acc;
        }, {});

        // Build preview file_data (few rows) for UI table
        const preview_file_data = preview_data.slice(1).map((row) => {
          return headers.reduce((obj, header, index) => {
            obj[header] = row[index] || "";
            return obj;
          }, {});
        });

        // Heavy pass: generate CSV string once for entire sheet using XLSX utils (efficient and streaming-friendly)
        // Note: xlsx.utils.sheet_to_csv is optimized and avoids building huge nested arrays
        // Yield before heavy CSV creation for this sheet
        await new Promise((r) => setTimeout(r, 0));
        const csvContent = xlsx.utils.sheet_to_csv(worksheet, {
          FS: ",",
          RS: "\n",
          blankrows: false,
        });
        const csvBlob = new Blob([csvContent], { type: "text/csv" });

        sheetsData[sheetName] = {
          headers,
          columnTypes,
          preview_file_data,
          csvBlob,
        };
        // Report progress (0-1)
        index += 1;
        if (typeof onProgress === "function") {
          try { onProgress(index / totalSheets); } catch (_) {}
        }
      } catch (sheetError) {
        console.error(`Error processing sheet ${sheetName}:`, sheetError);
        setUploadDatasetLoading(false);
        // Re-throw validation errors to be caught by the calling function
        throw sheetError;
      }
    }

    return sheetsData;
  } catch (error) {
    console.error("Error parsing Excel file:", error);
    data.showSnackbar(`parsing Excel file: ${error}`, "error");

    setUploadDatasetLoading(false);

    throw new Error(
      "Failed to parse Excel file. Please check if the file is valid and not corrupted."
    );
  }
};

export function getCurrentDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0"); // Add 1 because months are zero-based
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const mapRetrievalSequence = (key) => {
  const keyMapping = {
    basic: "Basic",
    "sentence-window": "Chunk Window",
    context_retrieval: "Context Retrieval",
    graphrag: "GraphRag With Vector Data",
    "graphrag+basic": "GraphRag With Standard Retrieval",
    "graphrag+sentence-window": "GraphRag With Chunk Window",
    "graphrag+context_retrieval": "GraphRag With Context Retrieval",
  };

  return keyMapping[key] || key; // Fallback to the original key if no match is found
};

export const calculateInitCostAndTime = async (
  setLoading_message,
  setLoading,
  organization,
  dispatch,
  user_id,
  bot_id,
  setInitCost,
  setOpenInitConfirmDialog,
  showSnackbar,
  navigate,
  currentApp
) => {
  setLoading_message(
    "Calculating the estimated cost for initialization. Please wait..."
  );
  setLoading(true);
  const orgId = organization?.organization?._id["$oid"];

  // Dynamically import to avoid pulling ESM-only axios during Jest tests
  const { getInitCost } = await import("../features/chatbots/chatbotSlice");
  const calculatedCostAndTime = await dispatch(
    getInitCost({
      orgId,
      userId: user_id,
      bot_id,
      toolId: currentApp.tools.retrieval[0]["$oid"],
    })
  );
  setInitCost(calculatedCostAndTime?.payload?.estimated_costs);
  if (calculatedCostAndTime?.payload?.status === "credits are enough") {
    setOpenInitConfirmDialog(true);
  } else {
    showSnackbar(
      "You don't have enough credits to initialize combinations. Goto 'Settings -> Usage' to purchase more credits.",
      "warning",
      <Button
        sx={{
          textTransform: "none",
          bgcolor: colors.primaryBlue,
          color: colors.extremeWhite,
          whiteSpace: "nowrap",
          "&:hover": { bgcolor: colors.secondaryBlue },
        }}
        onClick={() => navigate("/dashboard/settingsPage/usage")}
      >
        Purchase More Credits
      </Button>
    );
  }
  setLoading(false);
};

/**
 * Given a map of versionKeys → { updated_at: ISOString, … },
 * returns the key whose updated_at is newest.
 *
 * @param {Record<string, { updated_at: string }>} versions
 * @returns {string|null}
 */
export function getLatestVersionKey(versions) {
  if (!versions || typeof versions !== "object") return null;

  return (
    Object.keys(versions).sort((a, b) => {
      const da = new Date(versions[a].updated_at).getTime();
      const db = new Date(versions[b].updated_at).getTime();
      return db - da;
    })[0] || null
  );
}

export const handleLogoutTokenExpired = () => {
  localStorage.setItem("user", null);
  localStorage.setItem("token", null);

  alert("Session Expired. Please login again.");
  window.location.href = "/";
};
